import React, { useState, useEffect, useCallback } from 'react';
import { Tab, Nav, Form } from 'react-bootstrap';
import ModalCustom from '../../../UI/modal';
import Button from '../../../UI/button';
import ActivityAutocomplete, {
  ActivityAutocompleteModel
} from '../../../activity-autocomplete';
import {
  IBucketListItem,
  AddBucketListItemRequest,
  BucketListContentType,
  BucketListRegion
} from '../../../../Models/bucket-list/bucket-list.model';
import { useBucketList } from '../../../contexts/bucket-list-context';

type Props = {
  show: boolean;
  handleClose: () => void;
  editItem?: IBucketListItem | null;
};

const REGIONS = Object.values(BucketListRegion);

const ACTIVITY_TYPES = [
  'Restaurant',
  'Hotel',
  'Attraction',
  'Museum',
  'Park',
  'Beach',
  'Shopping',
  'Entertainment',
  'Transport',
  'Other'
];

const AddToBucketListModal: React.FC<Props> = ({
  show,
  handleClose,
  editItem
}) => {
  const { addItem, updateItem } = useBucketList();
  const isEditMode = !!editItem;

  // ── Edit form state ──────────────────────────────────────────────────────────
  const [editForm, setEditForm] = useState<Partial<AddBucketListItemRequest>>(
    {}
  );

  useEffect(() => {
    if (editItem) {
      setEditForm({
        contentType: editItem.contentType,
        title: editItem.title,
        description: editItem.description,
        notes: editItem.notes,
        region: editItem.region,
        activityType: editItem.activityType,
        websiteUrl: editItem.websiteUrl,
        link: editItem.link,
        imageUrl: editItem.imageUrl
      });
    }
  }, [editItem]);

  // ── Place Search tab state ───────────────────────────────────────────────────
  const [placeSelected, setPlaceSelected] =
    useState<Partial<AddBucketListItemRequest> | null>(null);
  const [placeNotes, setPlaceNotes] = useState('');

  // ── Link tab state ───────────────────────────────────────────────────────────
  const [linkForm, setLinkForm] = useState({
    title: '',
    link: '',
    description: '',
    region: BucketListRegion.Unknown as BucketListRegion
  });

  // ── Free Text tab state ──────────────────────────────────────────────────────
  const [textForm, setTextForm] = useState({
    title: '',
    description: '',
    notes: '',
    region: BucketListRegion.Unknown as BucketListRegion,
    activityType: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const resetForms = useCallback(() => {
    setPlaceSelected(null);
    setPlaceNotes('');
    setLinkForm({
      title: '',
      link: '',
      description: '',
      region: BucketListRegion.Unknown
    });
    setTextForm({
      title: '',
      description: '',
      notes: '',
      region: BucketListRegion.Unknown,
      activityType: ''
    });
    setSubmitError(null);
  }, []);

  const handleModalClose = () => {
    resetForms();
    handleClose();
  };

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handlePlaceSelected = (place: ActivityAutocompleteModel) => {
    setPlaceSelected({
      contentType: 'activity',
      title: place.name,
      lat: place.latitude,
      lng: place.longitude,
      googlePlaceId: place.googlePlacesId,
      placeId: place.placesId,
      types: place.types,
      websiteUrl: place.websiteUrl,
      address: place.description
    });
    setPlaceNotes('');
  };

  const handleSubmitPlace = async () => {
    if (!placeSelected?.title) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await addItem({
        ...(placeSelected as AddBucketListItemRequest),
        notes: placeNotes || undefined
      });
      handleModalClose();
    } catch (err: any) {
      setSubmitError(err?.message || 'Failed to add item');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitLink = async () => {
    if (!linkForm.title.trim() || !linkForm.link.trim()) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await addItem({
        contentType: 'research',
        title: linkForm.title.trim(),
        link: linkForm.link.trim(),
        description: linkForm.description.trim() || undefined,
        region: linkForm.region
      });
      handleModalClose();
    } catch (err: any) {
      setSubmitError(err?.message || 'Failed to add item');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitText = async () => {
    if (!textForm.title.trim()) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await addItem({
        contentType: 'raw_idea',
        title: textForm.title.trim(),
        description: textForm.description.trim() || undefined,
        notes: textForm.notes.trim() || undefined,
        region: textForm.region,
        activityType: textForm.activityType.trim() || undefined
      });
      handleModalClose();
    } catch (err: any) {
      setSubmitError(err?.message || 'Failed to add item');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitEdit = async () => {
    if (!editItem || !editForm.title?.trim()) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await updateItem(editItem.id, {
        ...editForm,
        title: editForm.title?.trim()
      });
      handleClose();
    } catch (err: any) {
      setSubmitError(err?.message || 'Failed to update item');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <ModalCustom
      show={show}
      onHide={handleModalClose}
      title={isEditMode ? 'Edit Bucket List Item' : 'Add to Bucket List'}
      size='lg'
    >
      {submitError && (
        <div className='alert alert-danger py-2 mb-3'>{submitError}</div>
      )}

      {isEditMode ? (
        // ── Edit Form ──────────────────────────────────────────────────────────
        <Form>
          <Form.Group className='mb-3'>
            <Form.Label>Title</Form.Label>
            <Form.Control
              type='text'
              value={editForm.title ?? ''}
              onChange={(e) =>
                setEditForm({ ...editForm, title: e.target.value })
              }
              placeholder='Title'
            />
          </Form.Group>
          <Form.Group className='mb-3'>
            <Form.Label>Description</Form.Label>
            <Form.Control
              as='textarea'
              rows={2}
              value={editForm.description ?? ''}
              onChange={(e) =>
                setEditForm({ ...editForm, description: e.target.value })
              }
              placeholder='Description (optional)'
            />
          </Form.Group>
          <Form.Group className='mb-3'>
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as='textarea'
              rows={2}
              value={editForm.notes ?? ''}
              onChange={(e) =>
                setEditForm({ ...editForm, notes: e.target.value })
              }
              placeholder='Notes (optional)'
            />
          </Form.Group>
          <div className='row'>
            <div className='col-6'>
              <Form.Group className='mb-3'>
                <Form.Label>Region</Form.Label>
                <Form.Select
                  value={editForm.region ?? BucketListRegion.Unknown}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      region: e.target.value as BucketListRegion
                    })
                  }
                >
                  {REGIONS.map((r) => (
                    <option
                      key={r}
                      value={r}
                    >
                      {r}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
            <div className='col-6'>
              <Form.Group className='mb-3'>
                <Form.Label>Activity Type</Form.Label>
                <Form.Control
                  type='text'
                  value={editForm.activityType ?? ''}
                  onChange={(e) =>
                    setEditForm({ ...editForm, activityType: e.target.value })
                  }
                  placeholder='e.g. Restaurant, Museum…'
                />
              </Form.Group>
            </div>
          </div>
          <Form.Group className='mb-3'>
            <Form.Label>Website URL</Form.Label>
            <Form.Control
              type='url'
              value={editForm.websiteUrl ?? ''}
              onChange={(e) =>
                setEditForm({ ...editForm, websiteUrl: e.target.value })
              }
              placeholder='https://...'
            />
          </Form.Group>
          <div className='d-flex justify-content-end mt-3'>
            <Button
              variant='primary'
              onClick={handleSubmitEdit}
              disabled={!editForm.title?.trim() || isSubmitting}
            >
              {isSubmitting ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </Form>
      ) : (
        // ── Add Form (3 tabs) ──────────────────────────────────────────────────
        <Tab.Container defaultActiveKey='place'>
          <Nav
            variant='tabs'
            className='mb-3'
          >
            <Nav.Item>
              <Nav.Link eventKey='place'>📍 Place Search</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey='link'>🔗 Paste Link</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey='text'>✏️ Free Text</Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            {/* ── Place Search Tab ───────────────────────────────────────── */}
            <Tab.Pane eventKey='place'>
              <ActivityAutocomplete
                label='Search for a place'
                placeholder='Type a restaurant, attraction, hotel…'
                setPlace={handlePlaceSelected}
              />
              {placeSelected && (
                <div className='bl-selected-place'>
                  <strong>{placeSelected.title}</strong>
                  {placeSelected.address && (
                    <div className='text-muted small mt-1'>
                      {placeSelected.address}
                    </div>
                  )}
                </div>
              )}
              {placeSelected && (
                <Form.Group className='mb-3 mt-2'>
                  <Form.Label>Notes (optional)</Form.Label>
                  <Form.Control
                    as='textarea'
                    rows={2}
                    value={placeNotes}
                    onChange={(e) => setPlaceNotes(e.target.value)}
                    placeholder='Why do you want to visit this place?'
                  />
                </Form.Group>
              )}
              <div className='d-flex justify-content-end mt-3'>
                <Button
                  variant='primary'
                  onClick={handleSubmitPlace}
                  disabled={!placeSelected?.title || isSubmitting}
                >
                  {isSubmitting ? 'Adding…' : 'Add to Bucket List'}
                </Button>
              </div>
            </Tab.Pane>

            {/* ── Paste Link Tab ─────────────────────────────────────────── */}
            <Tab.Pane eventKey='link'>
              <Form.Group className='mb-3'>
                <Form.Label>URL</Form.Label>
                <Form.Control
                  type='url'
                  value={linkForm.link}
                  onChange={(e) =>
                    setLinkForm({ ...linkForm, link: e.target.value })
                  }
                  placeholder='https://...'
                />
              </Form.Group>
              <Form.Group className='mb-3'>
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type='text'
                  value={linkForm.title}
                  onChange={(e) =>
                    setLinkForm({ ...linkForm, title: e.target.value })
                  }
                  placeholder='Give it a title'
                />
              </Form.Group>
              <Form.Group className='mb-3'>
                <Form.Label>Description (optional)</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={2}
                  value={linkForm.description}
                  onChange={(e) =>
                    setLinkForm({ ...linkForm, description: e.target.value })
                  }
                  placeholder='What is this link about?'
                />
              </Form.Group>
              <Form.Group className='mb-3'>
                <Form.Label>Region</Form.Label>
                <Form.Select
                  value={linkForm.region}
                  onChange={(e) =>
                    setLinkForm({
                      ...linkForm,
                      region: e.target.value as BucketListRegion
                    })
                  }
                >
                  {REGIONS.map((r) => (
                    <option
                      key={r}
                      value={r}
                    >
                      {r}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <div className='d-flex justify-content-end mt-3'>
                <Button
                  variant='primary'
                  onClick={handleSubmitLink}
                  disabled={
                    !linkForm.title.trim() ||
                    !linkForm.link.trim() ||
                    isSubmitting
                  }
                >
                  {isSubmitting ? 'Adding…' : 'Add to Bucket List'}
                </Button>
              </div>
            </Tab.Pane>

            {/* ── Free Text Tab ──────────────────────────────────────────── */}
            <Tab.Pane eventKey='text'>
              <Form.Group className='mb-3'>
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type='text'
                  value={textForm.title}
                  onChange={(e) =>
                    setTextForm({ ...textForm, title: e.target.value })
                  }
                  placeholder='What do you want to do or see?'
                />
              </Form.Group>
              <Form.Group className='mb-3'>
                <Form.Label>Description (optional)</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={2}
                  value={textForm.description}
                  onChange={(e) =>
                    setTextForm({ ...textForm, description: e.target.value })
                  }
                  placeholder='More details…'
                />
              </Form.Group>
              <Form.Group className='mb-3'>
                <Form.Label>Notes (optional)</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={2}
                  value={textForm.notes}
                  onChange={(e) =>
                    setTextForm({ ...textForm, notes: e.target.value })
                  }
                  placeholder='Personal notes…'
                />
              </Form.Group>
              <div className='row'>
                <div className='col-6'>
                  <Form.Group className='mb-3'>
                    <Form.Label>Region</Form.Label>
                    <Form.Select
                      value={textForm.region}
                      onChange={(e) =>
                        setTextForm({
                          ...textForm,
                          region: e.target.value as BucketListRegion
                        })
                      }
                    >
                      {REGIONS.map((r) => (
                        <option
                          key={r}
                          value={r}
                        >
                          {r}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className='col-6'>
                  <Form.Group className='mb-3'>
                    <Form.Label>Type (optional)</Form.Label>
                    <Form.Select
                      value={textForm.activityType}
                      onChange={(e) =>
                        setTextForm({
                          ...textForm,
                          activityType: e.target.value
                        })
                      }
                    >
                      <option value=''>Select type…</option>
                      {ACTIVITY_TYPES.map((t) => (
                        <option
                          key={t}
                          value={t}
                        >
                          {t}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
              </div>
              <div className='d-flex justify-content-end mt-3'>
                <Button
                  variant='primary'
                  onClick={handleSubmitText}
                  disabled={!textForm.title.trim() || isSubmitting}
                >
                  {isSubmitting ? 'Adding…' : 'Add to Bucket List'}
                </Button>
              </div>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      )}
    </ModalCustom>
  );
};

export default AddToBucketListModal;
